import {
  adoptFeatures,
  describe,
  downloadGeoJSON,
  featureCollection,
  fillStyle,
  polygonFeature,
  strokeStyle,
} from '../../common/geojson';

/**
 * @constant GEOJSON_MODULE
 * @description Module key, used to name the downloaded file.
 * @memberof module:Intertidal
 */
const GEOJSON_MODULE = 'intertidal';

/**
 * @function buildGeoJsonExport
 * @description Gathers the waterlines the analysis traced, each with the tide level it was taken
 * at, alongside the area of interest.
 * The tide level is what makes a waterline worth anything: a line without the level it was
 * observed at cannot be placed on a profile, so the layer metadata is matched back onto the
 * features by date before they are written.
 * The interpolated surface is not here - it is a raster, already offered as GeoTIFF from the
 * result page, and a vector format has nowhere to put it.
 * @param {Object} params
 * @param {Array<Array<Number>>} [params.coordinates] - Area of interest ring, `[lng, lat]` pairs.
 * @param {Object} [params.evaluatedEE] - The evaluated waterline FeatureCollection.
 * @param {Array<Object>} [params.waterlineLayers] - Per-date layer metadata, holding the tide level.
 * **Called by:
 * @see {ProcessingPage} - Intertidal's result page, through the GIS export icon.
 * @memberof module:Intertidal
 * @returns {Object} A FeatureCollection, empty when no waterline has been traced yet.
 */
export const buildGeoJsonExport = ({ coordinates, evaluatedEE, waterlineLayers }) => {
  const tideByDate = (waterlineLayers || []).reduce((acc, layer) => {
    if (layer?.date != null) acc[String(layer.date)] = layer.tideValue ?? layer.tide ?? null;
    return acc;
  }, {});

  const waterlines = adoptFeatures(evaluatedEE, 'waterlines').map((feature) => {
    const date = feature.properties?.date;
    const tide = date != null ? tideByDate[String(date)] : undefined;
    const when = date == null ? null : new Date(Number(date) || date);
    const label = when && !Number.isNaN(when.getTime()) ? `Waterline ${when.toISOString().slice(0, 10)}` : 'Waterline';

    return {
      ...feature,
      properties: {
        ...feature.properties,
        ...(tide === undefined ? {} : { tide }),
        ...describe(label, tide === undefined ? undefined : `Tide level ${tide}`),
        ...strokeStyle('#0288d1', { width: 2 }),
      },
    };
  });

  return featureCollection([
    polygonFeature(coordinates, 'area_of_interest', { ...describe('Area of interest'), ...fillStyle('#fdc669', 0) }),
    waterlines,
  ]);
};

/**
 * @function exportGeoJson
 * @description Builds the collection and hands it to the browser.
 * @param {Object} params - Same as {@link buildGeoJsonExport}.
 * **Called by:
 * @see {ProcessingPage} - Intertidal's result page.
 * @memberof module:Intertidal
 * @returns {Boolean} False when there was nothing to write.
 */
export const exportGeoJson = (params) => downloadGeoJSON(buildGeoJsonExport(params), GEOJSON_MODULE);
